import { IEditorV3 } from "@azu-business/editor-v3";
import { AibLineType, AsupInternalBlock, useArsContext } from "aic";
import { v3EditorProps } from "components/common/v3EditorProps";
import { useSave } from "contexts/appsettings/useSave";
import {
  useAppSettings,
  useHierarchyContext,
  useTlfContext,
  useTocContext,
} from "contexts/AppSettingsContext";
import { getExternalSingles } from "contexts/tlf/getExternalSingles";
import { UPDATE_TLF } from "contexts/tlf/interface";
import { MosaicTocEntryDto } from "contexts/toc/interface";
import { eHierarchyLevel } from "functions/api/enums";
import { isTitlesFootnotesReadDto } from "functions/api/tlfItem/TitlesFootnotesReadDto";
import { ReactNode } from "react";
import { CommentScope } from "../common/comments/CommentScope";

interface TitlesProps {
  firstLine?: number;
  lastLine?: number;
  noComments?: boolean;
  patch?: boolean;
  tocId?: string;
}

/**
 * Render TLF titles block with optional slicing and comment support.
 *
 * @param props - Destructured properties for titles.
 * @param props.firstLine - 1-based index of the first title line to include.
 * @param props.lastLine - 1-based index of the last title line to include.
 * @param props.noComments - When true, disables comment UI within the block.
 * @param props.patch - When true, persist changes via patch API instead of context dispatch.
 * @param props.tocId - Optional identifier for the ToC item associated with the titles.
 * @returns React node rendering the titles editor block.
 */
export const Titles = ({
  firstLine = 1,
  lastLine = 10,
  noComments = false,
  patch = false,
  tocId,
}: TitlesProps): ReactNode => {
  const appSettings = useAppSettings();
  const hierarchyContext = useHierarchyContext();
  const { state: tocState } = useTocContext();
  const { state: ars } = useArsContext();
  const tlfContext = useTlfContext();
  const { patchTlf, saveRequest } = useSave();
  const tlfData = tocId
    ? (tocState.tocItems.find((tlf) => tlf.id === tocId && isTitlesFootnotesReadDto(tlf)) as
        MosaicTocEntryDto | undefined)
    : tlfContext.tlfData;
  if (!hierarchyContext?.hierarchy || !tlfData || !tlfContext) return null;

  const externalSingles = getExternalSingles(
    tlfData.tocNumber,
    ars.analysisSets[tlfData.analysisSetId ?? ""]?.label ?? "",
    tlfData.releaseStatus,
    tlfData.domain,
    tlfData.singles,
    hierarchyContext.hierarchy.level,
    hierarchyContext.hierarchy.singles,
    hierarchyContext.hierarchy.domainReplacements,
    hierarchyContext.parentTemplates.singles,
    hierarchyContext.parentTemplates.domainReplacements,
  );

  return (
    <CommentScope
      id="titles-comment"
      activeContext="TlfItem"
      commentLabel="New titles comment"
      disabled={noComments}
    >
      <AsupInternalBlock<IEditorV3>
        id="titles"
        lineStyle={appSettings.defaultTableStyle}
        lines={tlfData.titles.slice(firstLine - 1, lastLine)}
        disabled={tlfContext.readOnly || (patch && saveRequest.requesting)}
        onChange={(ret) => {
          const newTitles = [
            ...tlfData.titles.slice(0, firstLine - 1),
            ...ret,
            ...tlfData.titles.slice(lastLine),
          ];
          if (patch) {
            patchTlf(tlfData.id, tlfData.azsolId, "titles", newTitles);
          } else {
            tlfContext.dispatch({
              operation: UPDATE_TLF,
              tlfData: { titles: newTitles },
              calledFrom: "Titles:Update",
            });
          }
        }}
        externalSingles={externalSingles}
        minLines={5 - firstLine}
        defaultType={AibLineType.centerOnly}
        editorProps={v3EditorProps}
        noComments={noComments}
        noTrafficLights={
          !tlfContext.showTrafficLights ||
          tlfData.primaryHierarchyLevel !== eHierarchyLevel.Report ||
          tlfData.coreTrafficLightSwitch === "DoNotCalculate"
        }
        changedBlocks={tlfData.changedTitles}
      />
    </CommentScope>
  );
};

Titles.displayName = "Titles";
